"use client";

import { useEffect, useRef, useState } from "react";
import { SITE } from "@/lib/site";

// OpenTable booking widget, same loader script as the live site (OpenTable replaced the old mural iframe).
// The loader writes its own iframe next to the script, so the script is added on the client into this slot
// (a <script> tag in JSX would not run).
//
// Speed (2026-10-22): most of the wait is OpenTable's own chain (loader -> iframe -> their booking app ->
// availability call). The reservations page preconnects to OpenTable in <head>, and this shows a placeholder the size of the
// widget until its iframe has loaded, with the phone number if it is slow.
export default function OpenTableWidget() {
  const slot = useRef(null);
  const [state, setState] = useState("loading"); // loading | slow | ready

  useEffect(() => {
    const el = slot.current;
    if (!el) return;

    // Fail-safes: the real widget is never kept hidden. It shows on its iframe's load event, or 4s after the
    // iframe appears, or after 20s whatever happens.
    const timers = [];
    const show = () => setState("ready");
    const watch = new MutationObserver(() => {
      const frame = el.querySelector("iframe");
      if (!frame) return;
      watch.disconnect();
      frame.addEventListener("load", show, { once: true });
      timers.push(setTimeout(show, 4000));
    });
    watch.observe(el, { childList: true, subtree: true });
    timers.push(setTimeout(() => setState((s) => (s === "loading" ? "slow" : s)), 8000));
    timers.push(setTimeout(show, 20000));

    if (el.querySelector("iframe")) show();
    if (!el.dataset.loaded) {
      el.dataset.loaded = "1";
      const s = document.createElement("script");
      s.type = "text/javascript";
      s.src = SITE.booking.opentableLoader;
      el.appendChild(s);
    }
    return () => { watch.disconnect(); timers.forEach(clearTimeout); };
  }, []);

  return (
    <div className="ot-wrap">
      {state !== "ready" && (
        <div className="ot-placeholder" role="status" aria-live="polite">
          <strong>Make a booking</strong>
          <span className="ot-placeholder__bar" />
          <span className="ot-placeholder__bar" />
          <span className="ot-placeholder__bar" />
          <span className="ot-placeholder__btn">
            {state === "slow" ? "Still loading…" : "Loading available tables…"}
          </span>
          {state === "slow" && (
            <p>
              Taking a while? Call <a href={SITE.phoneHref}>{SITE.phone}</a>
            </p>
          )}
        </div>
      )}
      <div className={`ot-slot ${state === "ready" ? "" : "ot-slot--hidden"}`} ref={slot} aria-label="Book a table at Lulu Wild via OpenTable" />
    </div>
  );
}
